import { Circle, CaretUp, MagnifyingGlass, X } from '@phosphor-icons/react';
import React, { useState, useRef, useEffect } from 'react';

interface ProjectOption {
  id: string;
  key: string;
  name: string;
}

interface ProjectDropdownProps {
  availableProjects: ProjectOption[];
  selectedProject: string;
  setSelectedProject: (id: string) => void;
  projectLatestReportEvaluated: Record<string, boolean>;
  isAdmin?: boolean;
}

export const ProjectDropdown: React.FC<ProjectDropdownProps> = ({
  availableProjects,
  selectedProject,
  setSelectedProject,
  projectLatestReportEvaluated,
  isAdmin = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const selected = availableProjects.find((p) => p.key === selectedProject);

  const filteredProjects =
    isAdmin && searchTerm
      ? availableProjects.filter(
          (project) =>
            project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            project.key.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : availableProjects;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-md border bg-white px-4 py-2 font-inter text-sm focus:outline-none focus:ring-0"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          {selected ? (
            <>
              {projectLatestReportEvaluated[selected.key] === false && (
                <Circle size={10} weight="fill" className="text-red-500" />
              )}
              {selected.name} ({selected.key})
            </>
          ) : (
            'Select Project'
          )}
        </span>
        <CaretUp
          size={18}
          className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white py-1 shadow-lg focus:outline-none"
          tabIndex={-1}
          role="listbox"
        >
          {isAdmin && (
            <li className="px-4 py-2">
              <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-2 py-1">
                <MagnifyingGlass size={16} weight="bold" />
                <input
                  type="text"
                  className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <span
                    className="cursor-pointer hover:text-brandRed-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                  >
                    <X size={16} weight="bold" />
                  </span>
                )}
              </div>
            </li>
          )}
          {filteredProjects.length === 0 && (
            <li
              className={`flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100`}
            >
              <span className="flex w-full flex-row items-center justify-between">
                <span>
                  {isAdmin && searchTerm
                    ? 'No projects match your search'
                    : 'No Projects Available'}
                </span>
              </span>
            </li>
          )}
          {filteredProjects.map((project) => (
            <li
              key={project.key}
              className={`flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100 ${
                project.key === selectedProject
                  ? 'bg-primary-50 font-semibold'
                  : ''
              }`}
              onClick={() => {
                setSelectedProject(project.key);
                setOpen(false);
                setSearchTerm('');
              }}
              role="option"
              aria-selected={project.key === selectedProject}
            >
              <span className="flex w-full flex-row items-center justify-between">
                <span>
                  {project.name} ({project.key})
                </span>
                {projectLatestReportEvaluated[project.key] === false && (
                  <Circle size={10} weight="fill" className="text-red-500" />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
